/* eslint-disable camelcase */
import React, { FC, useEffect, useState } from 'react';
import { validateLatitudeLongitude } from '@deckee/utilities';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { captureEvent } from '../../../../../services/event-capture';
import { AppState } from '../../../../../services/store';
import {
  hideAllLayers,
  hideBottomControlPane,
  hideSearchButton,
  moveMapToLocation,
  showSelectMapPoint,
  selectCurrentMapCenter,
} from '../../../../../services/store/modules/app-controls';
import {
  selectPointById,
  selectPoints,
  updatePoint,
} from '../../../../../services/store/modules/plan';
import LocationSelectorLayout from '../../../../layouts/LocationSelectorLayout/LocationSelectorLayout';
import useMapPlanPoints from '../../hooks/use-map-plan-points';
import { telemetry } from '../../../../../services/backend';
import { hasFeatureEnabled } from '../../../../../services/feature';
import { useRadioRanges } from '../../../Prepare/hooks/useRadioRanges';
import { Box, Button, Paragraph } from '@deckee/deck-hand';
import { formatLatLon } from '../../../../helpers/coordinate-helpers';
import RadioChannelsMessageBlock from '../../../../shared/RadioChannelsMessageBlock/RadioChannelsMessageBlock';
import { useRadioRangeLayers } from '../../../../hooks/use-radio-range-layers';

interface ChooseLocationPageProps {
  pointId: string;
}

const ChooseLocationPage: FC<ChooseLocationPageProps> = ({ pointId }) => {
  const history = useHistory();
  const point = useSelector(selectPointById(pointId));
  const planPoints = useSelector(selectPoints);
  const [hasError, setError] = useState(false);
  const currentMapCenter = useSelector(selectCurrentMapCenter);

  const getStepType = (pointOrdinal?: number) => {
    if (pointOrdinal === 0) return 'Start point';
    if (pointOrdinal === planPoints.length - 1) return 'End point';

    return 'Waypoint';
  };

  const handleBack = () => {
    history.goBack();
  };
  const dispatch = useDispatch();

  const { time_type } = useSelector((state: AppState) => state.floatPlan);

  useMapPlanPoints();

  // Get radio ranges for current map center
  const { radioChannels, isEnabled, isLoading } =
    useRadioRanges(currentMapCenter);

  useRadioRangeLayers(window?.deckeeMap);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const map = window?.deckeeMap;

  useEffect(() => {
    if (
      point?.latitude &&
      point?.longitude &&
      validateLatitudeLongitude(point.latitude, point.longitude)
    ) {
      dispatch(
        moveMapToLocation({
          center: {
            lat: point.latitude,
            lon: point.longitude,
          },
        }),
      );
    } else if (point?.id) {
      // This happends when selecting Choose on Map.
      setError(false);
    } else {
      setError(true);
      telemetry(
        telemetry.ERROR,
        `[Plan.chooseLocationPage] Error validating selected point ${JSON.stringify(
          point,
        )}`,
      );
    }

    // display en error and log it
  }, [dispatch, point, point?.latitude, point?.longitude]);

  useEffect(() => {
    dispatch(hideSearchButton(false));
    dispatch(hideAllLayers(true));
    dispatch(hideBottomControlPane(false));

    return () => {
      dispatch(hideSearchButton(true));
      dispatch(hideAllLayers(false));
      dispatch(showSelectMapPoint(0)); // ;(
      dispatch(hideBottomControlPane(true));
      map?.setLayoutProperty(
        'water-body-definitions fill',
        'visibility',
        'none',
      );
    };
  }, [dispatch, map]);

  const handleSelectLocation = ({ lat, lon }: { lat: number; lon: number }) => {
    captureEvent({
      type: 'plan: choose location',
      payload: {},
      sendToAmplitude: true,
    });

    // The places results are turned with a lower accuracy than when we choose from the map,
    // so trimming to 4 decimal places in order for them to match
    const DECIMAL_PLACES = 4;
    const name =
      parseFloat(`${point?.latitude}`)?.toFixed(DECIMAL_PLACES) ===
        lat?.toFixed(DECIMAL_PLACES) &&
      parseFloat(`${point?.longitude}`)?.toFixed(DECIMAL_PLACES) ===
        lon?.toFixed(DECIMAL_PLACES)
        ? point?.name
        : '';

    dispatch(
      updatePoint({
        id: pointId,
        latitude: lat,
        longitude: lon,
        name,
        isRoundTrip: time_type !== 'arrival',
      }),
    );
    history.push('/maps/plan/details');
  };
  return (
    <LocationSelectorLayout
      onSelectLocation={handleSelectLocation}
      isInvalidLocation={hasError}
      onBack={handleBack}
      buttonLabel="Confirm"
      heading={getStepType(point?.ordinal)}
      instructionText={point?.name || ''}
      render={({ buttonLabel, handleOnComplete }) => (
        <Box
          px="3"
          mb="3"
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Paragraph pb="3" textAlign="center">
            {currentMapCenter?.lat && currentMapCenter?.lon
              ? formatLatLon(
                  'decimal',
                  currentMapCenter.lat,
                  currentMapCenter.lon,
                )
              : ''}
          </Paragraph>

          {isEnabled && (
            <Box width="100%">
              <RadioChannelsMessageBlock
                isLoading={isLoading}
                radioChannels={radioChannels}
              />
            </Box>
          )}

          <Button wide mt={3} disabled={hasError} onClick={handleOnComplete}>
            {buttonLabel}
          </Button>
        </Box>
      )}
    />
  );
};

export default ChooseLocationPage;
